import { useTranslation } from "react-i18next";

import moment from "moment";

import InfoIconWithTooltip from "@/components/IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";

import {
  COLUMN_RESOURCES,
  DATE_FORMATS,
  DOWNLOAD_ACTIVITY_STATUS,
  useDateTime
} from "../..";
import DetailsCardItemText from "../../../components/DetailsSection/components/DetailsContent/components/DetailsCardItemText";
import SharedStatusBadge from "../../../components/SharedStatusBadge/SharedStatusBadge";
import Tooltip from "../../../components/Tooltip/Tooltip";
import DateTime from "../../components/dateTime/DateTime";
import {
  getNumberFromString,
  getStringWithoutNumber
} from "../../functions/functions";

const useCustomResources = () => {
  const { t } = useTranslation();

  const {
    currentTimeFormatHoursMinutes,
    currentTimeZone,
    convertToCurrentTimezone
  } = useDateTime();

  const renderLastDownloadAttemptBadge = (
    wasLastAttemptSuccessful: boolean | null
  ) => {
    if (wasLastAttemptSuccessful === null) {
      return <DetailsCardItemText textToRender={"-"} />;
    }
    if (wasLastAttemptSuccessful === true) {
      return (
        <SharedStatusBadge
          status={DOWNLOAD_ACTIVITY_STATUS.Successful}
          resourceName={COLUMN_RESOURCES.LastDownload}
        />
      );
    } else {
      return (
        <SharedStatusBadge
          status={DOWNLOAD_ACTIVITY_STATUS.Failed}
          resourceName={COLUMN_RESOURCES.LastDownload}
        />
      );
    }
  };

  const renderLastDownloadAttemptBadgeWithTooltip = (data: any) => {
    return data.lastDownloadAttemptDate ? (
      <Tooltip
        data-testid="last-download-attempt-tooltip"
        title={<DateTime date={String(data.lastDownloadAttemptDate)} />}
        placement="top"
        size="medium"
        hasArrow
      >
        <span>{renderLastDownloadAttemptBadge(data.lastDownloadAttempt)}</span>
      </Tooltip>
    ) : (
      <>{renderLastDownloadAttemptBadge(data.lastDownloadAttempt)}</>
    );
  };

  const renderNextDownloadAttemptText = (
    nextDownloadAttempt: string | null,
    fromTable?: boolean
  ) => {
    if (nextDownloadAttempt === null) {
      return <DetailsCardItemText textToRender={"-"} />;
    } else {
      const nextDownloadAttemptTextDetails = getNumberFromString(
        nextDownloadAttempt
      )
        ? // display translation with dynamicaly fetched number
          t(`Details##${getStringWithoutNumber(nextDownloadAttempt)}`, {
            count: +getNumberFromString(nextDownloadAttempt)
          })
        : // else translate string
          t(`Details##${nextDownloadAttempt}`);

      return fromTable ? (
        convertStringToDate(nextDownloadAttempt)
      ) : (
        <DetailsCardItemText textToRender={nextDownloadAttemptTextDetails} />
      );
    }
  };

  const getConvertedStringToDate = (dateString: string | null) => {
    if (!moment(dateString, DATE_FORMATS.ISO_8601).isValid()) return null;

    const convertedDate = convertToCurrentTimezone(dateString);
    return convertedDate.format(currentTimeFormatHoursMinutes);
  };

  const convertStringToDate = (nextDownloadAttempt: string | null) => {
    if (!moment(nextDownloadAttempt, DATE_FORMATS.ISO_8601).isValid()) {
      return nextDownloadAttempt;
    }

    const convertedDate = convertToCurrentTimezone(nextDownloadAttempt);
    const currentTime = moment().tz(currentTimeZone);

    return convertedDate.isBefore(currentTime)
      ? t("Details##Upon device connection")
      : getConvertedStringToDate(nextDownloadAttempt);
  };

  const renderMethodText = (method: string) =>
    t(`Details##integration method##${method}`);

  const renderTextOrNotAvailable = (textValue: string | null) =>
    textValue && textValue !== "" ? textValue : t("General##Undefined");

  const renderImeiOrTooltip = (textValue?: string | null) => {
    if (!textValue) {
      return (
        <InfoIconWithTooltip
          iconTestId="empty-imei-info-icon"
          title={t(`Table##device imei empty`)}
        />
      );
    }

    return textValue;
  };

  return {
    getConvertedStringToDate,
    renderLastDownloadAttemptBadge,
    renderNextDownloadAttemptText,
    renderLastDownloadAttemptBadgeWithTooltip,
    renderMethodText,
    renderTextOrNotAvailable,
    renderImeiOrTooltip
  };
};

export default useCustomResources;
